import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import {
  WSActions,
  WSCard,
  WSElement,
  WSInfoBox,
  WSList,
  WSProgressBar,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { VerificationLevel } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import {
  queriesNotifications,
  queriesOnboarding,
  queriesPersons
} from "@query";
import { useSession } from "@hooks/useSession";
import { footprintAppearanceConfig } from "../../../../shared/constants/footprint";
import { ConfirmationDisclosures } from "../../components/ConfirmationDisclosures";
import { ReviewAccountType } from "../../components/ReviewAccountType";
import { ReviewBusinessInformation } from "../../components/ReviewBusinessInformation";
import { ReviewPerson } from "../../components/ReviewPerson";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { OnboardingModuleCreateAccount } from "../../types";
import { CreateAccountContext } from "./types";
import { useMutationSubmit } from "./useMutationSubmit";
import { useRedirectToAccountType } from "./useRedirectToAccountType";
import { ReviewUSPerson } from "../../components/ReviewUSPerson";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

const openFootprintVerification = (token: string, onComplete?: () => void) => {
  const component = footprint.init({
    kind: FootprintComponentKind.Verify,
    variant: "modal",
    authToken: token,
    onComplete: () => {
      WSQueryCache.invalidateQueries(
        queriesOnboarding.getOnboardingStatus.queryKeyBase
      );
      WSQueryCache.invalidateQueries(
        queriesOnboarding.getMissingFields.queryKeyBase
      );
      WSQueryCache.invalidateQueries(
        queriesNotifications.listNotifications.queryKeyBase
      );

      onComplete?.();
    },
    options: {
      showLogo: false
    },
    appearance: footprintAppearanceConfig
  });
  component.render();
};

export const Review: React.FC<FlowStep> = ({ onBack, onNext }) => {
  const history = useHistory();
  const createAcccountPath = history.location.pathname.replace("/review", "");
  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;

  const verificationLevel =
    onboardingModule?.options?.verificationLevel || "Tax";
  const includeBeneficialOwners = verificationLevel === "Banking";

  const [context] = useOnboardingContext<CreateAccountContext>();
  const [submit, meta] = useMutationSubmit({
    onSuccess: ({ status, missingData }) => {
      const hasMissingFields =
        missingData.requiredFields && missingData.requiredFields?.length > 0;

      const shouldGoBackToEditPersonalInfo =
        status === VerificationLevel.UPDATE_REQUIRED && hasMissingFields;

      if (shouldGoBackToEditPersonalInfo) {
        if (context.accountType?.type === "Individual") {
          history.push(createAcccountPath + "/personal-information");
        } else {
          history.push(createAcccountPath + "/business-information");
        }
      } else if (missingData.completeOnboardingToken) {
        openFootprintVerification(missingData.completeOnboardingToken, onNext);
      } else {
        onNext?.();
      }
    }
  });
  const [consent, setConsent] = useState(false);
  const session = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({
    personId: session.personId
  });

  useRedirectToAccountType();

  if (meta.isLoading) {
    return (
      <WSList gap="2XL">
        <WSCard>
          <WSSectionToolbar title="Verifying your Tax ID" />
          <ProgressBar time={30000} />
          <WSInfoBox mt="M">
            We are verifying your Tax ID. This can take up to a minute. Do not
            close this tab while verification is in progress.
          </WSInfoBox>
        </WSCard>

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Continue",
              disabled: true
            }
          ]}
        />

        <WSText.ParagraphXs color="gray400">
          The information on this page is required to setup your Wingspan
          account, if you exit or leave without completing this step, you will
          return to this page the next time you sign-in
        </WSText.ParagraphXs>
      </WSList>
    );
  }

  return (
    <WSList gap="2XL">
      <WSList gap="L">
        <WSElement>
          <WSSectionToolbar title="Confirm account information" />
          <WSText.ParagraphSm mt="L" color="gray500">
            Please review to ensure the information is accurate before
            submitting
          </WSText.ParagraphSm>
        </WSElement>

        {context.accountType && (
          <ReviewAccountType
            formData={context.accountType}
            onEdit={() => {
              history.push(createAcccountPath + "/account-type");
            }}
          />
        )}

        {context.accountType?.type === "Individual" ? (
          <>
            <ReviewPerson
              title="Personal information"
              formData={context.personalInformation.person}
              showOwnershipStake={includeBeneficialOwners}
              onEdit={() => {
                history.push(createAcccountPath + "/personal-information");
              }}
            />
            {context.accountType.isUsPerson &&
            context.usInternationalPersonInformation?.usPerson?.firstName ? (
              <ReviewUSPerson
                title="U.S person information"
                formData={context.usInternationalPersonInformation?.usPerson}
                onEdit={() => {
                  history.push(
                    createAcccountPath + "/international-us-person-information"
                  );
                }}
              />
            ) : null}
          </>
        ) : (
          <>
            {context.businessInformation && (
              <ReviewBusinessInformation
                formData={context.businessInformation}
                country={context.accountType?.country || "US"}
                onEdit={() => {
                  history.push(createAcccountPath + "/business-information");
                }}
                personalEmail={queryPerson.data?.email || undefined}
              />
            )}

            {context.representative && (
              <ReviewPerson
                title="Authorized representative information (You)"
                formData={context.representative.representative}
                showOwnershipStake={includeBeneficialOwners}
                onEdit={() => {
                  history.push(createAcccountPath + "/representative");
                }}
              />
            )}

            {includeBeneficialOwners &&
              (context.representative?.beneficialOwners || []).map(
                (beneficialOwner, index) => (
                  <ReviewPerson
                    key={String(index)}
                    title={`Beneficial owner (${index + 1})`}
                    formData={beneficialOwner}
                    onEdit={() => {
                      history.push(createAcccountPath + "/representative");
                    }}
                    showOwnershipStake
                  />
                )
              )}
          </>
        )}
        <WSCard>
          <ConfirmationDisclosures
            consent={consent}
            setConsent={setConsent}
            verificationLevel={onboardingModule?.options?.verificationLevel}
          />
        </WSCard>
      </WSList>

      <ErrorAlert context="MonitoredEntity" error={meta.error} />

      <WSActions
        alignment="fill"
        buttons={[
          { label: "Back", onClick: onBack, kind: "Secondary" },
          {
            label: "Confirm & continue",
            disabled: !consent,
            onClick: () => {
              submit();
            }
          }
        ]}
      />

      <WSText.ParagraphXs color="gray400">
        The information on this page is required to setup your Wingspan account,
        if you exit or leave without completing this step, you will return to
        this page the next time you sign-in
      </WSText.ParagraphXs>
    </WSList>
  );
};

const DEFAULT_TIME = 10000;
const DEFAULT_REFRESH_INTERVAL = 250;

const ProgressBar: React.FC<{
  time?: number;
  refreshInterval?: number;
}> = props => {
  const [progress, setProgress] = useState(0);

  const time = useMemo(() => props.time || DEFAULT_TIME, [props.time]);
  const refreshInterval = useMemo(
    () => props.refreshInterval || DEFAULT_REFRESH_INTERVAL,
    [props.refreshInterval]
  );

  const step = useMemo(
    () => Math.round(100 / (time / refreshInterval)),
    [refreshInterval, time]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 90) {
          clearInterval(interval);
        }

        return p + step;
      });
    }, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval, step]);

  return <WSProgressBar percent={progress} />;
};
